import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";

const LINKS = [
  { href: "#about", label: "About" },
  { href: "#services", label: "Services" },
  { href: "#gallery", label: "Gallery" },
  { href: "#testimonials", label: "Testimonials" },
  { href: "#contact", label: "Contact" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-[#E5E1D8] shadow-[0_8px_32px_rgba(44,76,59,0.05)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <a
            href="#top"
            data-testid="nav-logo"
            className="flex items-center gap-3 group"
          >
            <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full font-heading text-xl leading-none transition-colors ${
              scrolled ? "bg-[#2C4C3B] text-[#F4F1EA]" : "bg-[#F4F1EA] text-[#2C4C3B]"
            }`}>
              C
            </span>
            <span className="hidden sm:flex flex-col leading-tight">
              <span className={`font-heading text-xl transition-colors ${
                scrolled ? "text-[#1C2A24]" : "text-white text-shadow-soft"
              }`}>
                Carlene&rsquo;s
              </span>
              <span className={`text-[11px] tracking-[0.25em] uppercase font-semibold transition-colors ${
                scrolled ? "text-[#667B68]" : "text-[#F4F1EA]/90 text-shadow-soft"
              }`}>
                Comfort Care
              </span>
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-10">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={`text-[15px] font-medium transition-colors relative after:content-[''] after:absolute after:-bottom-1 after:left-0 after:w-0 after:h-px hover:after:w-full after:transition-all after:duration-300 ${
                  scrolled
                    ? "text-[#3E574B] hover:text-[#2C4C3B] after:bg-[#2C4C3B]"
                    : "text-[#F4F1EA] hover:text-white after:bg-[#F4F1EA] text-shadow-soft"
                }`}
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="tel:+15033942321"
              data-testid="nav-call-btn"
              className={`inline-flex items-center gap-2 transition-colors rounded-full px-6 py-3 font-medium text-[14px] ${
                scrolled
                  ? "bg-[#2C4C3B] text-white hover:bg-[#1f372a]"
                  : "bg-[#F4F1EA] text-[#1C2A24] hover:bg-white"
              }`}
            >
              <Phone className="w-4 h-4" aria-hidden />
              (503) 394-2321
            </a>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#F4F1EA] text-[#2C4C3B]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {open && (
          <div
            data-testid="nav-mobile-panel"
            className="md:hidden pb-6 pt-2 border-t border-[#E5E1D8]"
          >
            <div className="flex flex-col gap-2">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className="py-3 px-2 text-[#1C2A24] font-medium border-b border-[#E5E1D8]/60"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="tel:+15033942321"
                data-testid="nav-mobile-call-btn"
                className="mt-3 inline-flex items-center justify-center gap-2 bg-[#2C4C3B] text-white rounded-full px-6 py-3 font-medium"
              >
                <Phone className="w-4 h-4" aria-hidden />
                (503) 394-2321
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
